import React, { useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, Loader2, X, GripVertical, ImagePlus } from "lucide-react";
import { adminApi } from "../api";

/**
 * Drag-and-drop multi-image uploader, Shopify-style.
 * - Accepts multiple files, uploads directly to Cloudinary using signed params
 * - Shows progress per image
 * - Reorder via drag, remove individually
 * - `value` is an array of Cloudinary secure_urls; `onChange` returns the updated array
 */
export default function ImageUploader({ value = [], onChange, folder = "asvasa/products", max = 10, testid = "image-uploader", guidelines = null }) {
  const fileInput = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({}); // { tempId: 0-100 }
  const [dragOver, setDragOver] = useState(false);
  const dragIdx = useRef(null);

  const images = Array.isArray(value) ? value.filter(Boolean) : [];

  const getSignature = useCallback(async () => {
    const { data } = await adminApi.get(`/admin/uploads/signature?folder=${encodeURIComponent(folder)}`);
    return data;
  }, [folder]);

  const uploadOne = useCallback(async (file, sig, tempId) => {
    const form = new FormData();
    form.append("file", file);
    form.append("api_key", sig.api_key);
    form.append("timestamp", sig.timestamp);
    form.append("signature", sig.signature);
    form.append("folder", sig.folder);

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", `https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`);
      xhr.upload.onprogress = (ev) => {
        if (ev.lengthComputable) {
          const pct = Math.round((ev.loaded / ev.total) * 100);
          setProgress((p) => ({ ...p, [tempId]: pct }));
        }
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(JSON.parse(xhr.responseText));
        } else {
          reject(new Error(`Upload failed (${xhr.status})`));
        }
      };
      xhr.onerror = () => reject(new Error("Network error"));
      xhr.send(form);
    });
  }, []);

  const handleFiles = useCallback(
    async (fileList) => {
      const files = Array.from(fileList || []).filter((f) => f.type.startsWith("image/"));
      if (!files.length) return;
      const remaining = Math.max(0, max - images.length);
      const toUpload = files.slice(0, remaining);
      if (files.length > remaining) {
        toast.warning(`You can only upload ${max} images. Uploading first ${remaining}.`);
      }
      if (!toUpload.length) return;

      setUploading(true);
      try {
        const sig = await getSignature();
        const uploaded = [];
        for (const file of toUpload) {
          const tempId = `${file.name}-${Date.now()}-${Math.random()}`;
          setProgress((p) => ({ ...p, [tempId]: 1 }));
          try {
            const res = await uploadOne(file, sig, tempId);
            uploaded.push(res.secure_url);
            setProgress((p) => {
              const np = { ...p };
              delete np[tempId];
              return np;
            });
          } catch (err) {
            toast.error(`Failed to upload ${file.name}`);
            setProgress((p) => {
              const np = { ...p };
              delete np[tempId];
              return np;
            });
          }
        }
        if (uploaded.length) {
          onChange([...images, ...uploaded]);
          toast.success(`${uploaded.length} image${uploaded.length > 1 ? "s" : ""} uploaded`);
        }
      } catch (err) {
        toast.error("Could not start upload. Please retry.");
      } finally {
        setUploading(false);
      }
    },
    [images, max, getSignature, uploadOne, onChange]
  );

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  const removeImage = (idx) => {
    const next = images.filter((_, i) => i !== idx);
    onChange(next);
  };

  const onDragStart = (i) => (e) => {
    dragIdx.current = i;
    e.dataTransfer.effectAllowed = "move";
  };

  const onDragOver = (i) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const onDropSort = (i) => (e) => {
    e.preventDefault();
    const from = dragIdx.current;
    dragIdx.current = null;
    if (from == null || from === i) return;
    const next = images.slice();
    const [moved] = next.splice(from, 1);
    next.splice(i, 0, moved);
    onChange(next);
  };

  const activeUploads = Object.values(progress);

  return (
    <div data-testid={testid}>
      {guidelines && (
        <div className="mb-3 px-4 py-2.5 bg-[#1A1A1A]/60 border-l-2 border-[#c3ba9e]/60 text-[11px] tracking-wider text-[#9a9787]" data-testid={`${testid}-guidelines`}>
          <span className="text-[#c3ba9e]">Recommended:</span> {guidelines}
        </div>
      )}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => fileInput.current?.click()}
        className={`cursor-pointer border-2 border-dashed transition-colors px-6 py-10 text-center ${
          dragOver ? "border-[#c3ba9e] bg-[#1f1f21]" : "border-[#2a2a2d] bg-[#0d0d0e] hover:border-[#c3ba9e]/50"
        }`}
        data-testid={`${testid}-dropzone`}
      >
        {uploading ? (
          <div className="flex flex-col items-center gap-2 text-[#c3ba9e]">
            <Loader2 className="animate-spin" size={22} />
            <p className="text-[11px] tracking-[0.28em] uppercase">Uploading…</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2 text-[#8c8a7e]">
            <UploadCloud size={26} />
            <p className="text-[12px] tracking-wider">
              Drag & drop or <span className="text-[#c3ba9e] underline">browse</span>
            </p>
            <p className="text-[10px] text-[#5e5d54]">PNG, JPG, WEBP · up to {max} images</p>
          </div>
        )}
        <input
          ref={fileInput}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
          data-testid={`${testid}-input`}
        />
      </div>

      {activeUploads.length > 0 && (
        <div className="mt-3 space-y-1.5">
          {Object.entries(progress).map(([id, pct]) => (
            <div key={id} className="h-1 bg-[#1f1f21] overflow-hidden">
              <div className="h-full bg-[#c3ba9e] transition-all" style={{ width: `${pct}%` }} />
            </div>
          ))}
        </div>
      )}

      {images.length > 0 && (
        <div className="mt-5 grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-3" data-testid={`${testid}-grid`}>
          {images.map((url, i) => (
            <div
              key={`${url}-${i}`}
              draggable
              onDragStart={onDragStart(i)}
              onDragOver={onDragOver(i)}
              onDrop={onDropSort(i)}
              className="relative group aspect-square bg-[#1f1f21] overflow-hidden border border-[#2a2a2d]"
              data-testid={`${testid}-item-${i}`}
            >
              <img src={url} alt="" className="w-full h-full object-cover" />
              {i === 0 && (
                <span className="absolute top-1.5 left-1.5 bg-[#c3ba9e] text-[#0d0d0e] text-[9px] tracking-[0.22em] uppercase px-1.5 py-0.5">
                  Cover
                </span>
              )}
              <button
                type="button"
                onClick={() => removeImage(i)}
                className="absolute top-1.5 right-1.5 bg-[#0d0d0e]/70 text-[#e9e6db] p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`${testid}-remove-${i}`}
              >
                <X size={12} />
              </button>
              <span className="absolute bottom-1.5 right-1.5 text-[#c3ba9e] opacity-0 group-hover:opacity-60 cursor-move">
                <GripVertical size={14} />
              </span>
            </div>
          ))}
          {images.length < max && (
            <button
              type="button"
              onClick={() => fileInput.current?.click()}
              className="aspect-square border border-dashed border-[#2a2a2d] text-[#5e5d54] hover:text-[#c3ba9e] hover:border-[#c3ba9e]/50 flex items-center justify-center transition-colors"
              data-testid={`${testid}-add-more`}
            >
              <ImagePlus size={18} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
